import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSWRImmutable from 'swr/immutable'
import { get } from 'aws-amplify/api'

import SpecialitiesContext from '../context/SpecialitiesContext'
import HeaderContext from '../context/HeaderContext'
import { buildPatientsApiUrl } from '../utils/patientsApiBaseUrl'
import errorTrackingService from '../services/errorTrackingService'

const SpecialitiesProvider = ({ children }) => {
  const { getIdTokenClaims } = useAuth0()
  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const [currentView, setCurrentView] = useState('')
  const [dataSpecialtiesInfo, setDataSpecialtiesInfo] = useState(null)
  const [serviceDetail, setServiceDetail] = useState(null)
  const [specialtyId, setSpecialtyId] = useState(null)
  const { userInfo } = useContext(HeaderContext)

  const { data, isLoading } = useSWRImmutable(
    userInfo.mode ? `getSpecialtiesInfo_${userInfo.mode}` : null,
    (args) => getSpecialtiesInfo(args)
  )

  const getSpecialtiesInfo = async () => {
    const idToken = await getIdTokenClaims()
    try {
      const resp = get({
        apiName: baseUrl,
        path: '/api/v2/specialties',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()
      return data
    } catch (error) {
      console.error('Error al obtener especialidades:', error)
      // Retornar estructura consistente en caso de error
      return { data: { specialties: [] } }
    }
  }

  const getSpecialtyServices = useCallback(async () => {
    const endpoint = buildPatientsApiUrl(
      `/v1/specialties/${specialtyId}/services`
    )
    if (!endpoint) {
      console.error(
        'Missing REACT_APP_PATIENTS_API_BASE_URL for specialty services'
      )
      return { data: [] }
    }

    const idToken = await getIdTokenClaims()
    if (!idToken?.__raw) {
      console.error('Missing auth token for specialty services')
      return { data: [] }
    }

    try {
      const response = await fetch(endpoint, {
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${idToken.__raw}`
        }
      })
      const data = await response.json()

      if (!response.ok) {
        const error = new Error(
          data?.message || 'Error al obtener servicios de especialidad'
        )
        error.isAPIError = true
        error.response = { status: response.status, data }
        errorTrackingService.trackAPIError(error, endpoint, 'GET')
        return { data: [] }
      }

      return data
    } catch (error) {
      console.error('Error al obtener servicios de especialidad:', error)
      return { data: [] }
    }
  }, [getIdTokenClaims, specialtyId])

  const getServiceDetail = useCallback(
    async (serviceId) => {
      const idToken = await getIdTokenClaims()
      try {
        const resp = get({
          apiName: baseUrl,
          path: `/api/v2/services?plenna_id=${serviceId}`,
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${idToken.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()
        setServiceDetail(data)
        return data
      } catch (error) {
        console.error('Error al obtener detalle del servicio:', error)
        setServiceDetail(null)
        return null
      }
    },
    [getIdTokenClaims, baseUrl]
  )

  useEffect(() => {
    if (!isLoading && data) {
      // Agregar validaciones de seguridad para evitar errores de acceso a propiedades indefinidas
      if (data.data && data.data.specialties) {
        setDataSpecialtiesInfo(data.data.specialties)
      } else {
        console.warn('La respuesta de la API no tiene la estructura esperada:', data)
        setDataSpecialtiesInfo([])
      }
    }
  }, [data, isLoading])

  return (
    <SpecialitiesContext.Provider
      value={{
        data,
        isLoading,
        dataSpecialtiesInfo,
        getServiceDetail,
        serviceDetail,
        currentView,
        setCurrentView,
        specialtyId,
        specialty_id: specialtyId,
        setSpecialtyId,
        setSpecialty_id: setSpecialtyId,
        getSpecialtyServices
      }}
    >
      {children}
    </SpecialitiesContext.Provider>
  )
}

export default SpecialitiesProvider
